/**
 * 注釈付き遺伝子マップ（U150 / U151 / U156 / U158 / U161）。
 *
 * - 上段: 分子全体と現在の表示窓（染色体級での現在地）
 * - 中段: 目盛り（縮尺に応じて 1・2・5 系の刻み）
 * - 下段: 注釈レーン（可視範囲だけ空間索引から引き、溢れは間引き）
 *
 * Ctrl/⌘+ホイールで連続ズーム、ホイールで横移動、Shift+ドラッグで範囲選択。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FeatureIndex,
  type GenomicFeatureV1,
  type Interval,
  type Topology,
  type ViewRange,
  basesPerPixel,
  chooseTickStep,
  formatAxisLabel,
  formatCompactBase,
  panRange,
  tickPositions,
  zoomRange,
} from '@kensuke/genome-core';
import type { SequenceOverlayMark } from './geneAnalysisAnalysis';
import {
  baseToPixel,
  canFitLabel,
  featureColor,
  packFeatureLanes,
  pixelToBase,
  truncateLabel,
} from './geneAnalysisLayout';

const OVERVIEW_HEIGHT = 22;
const RULER_HEIGHT = 26;
const LANE_HEIGHT = 18;
const LANE_GAP = 4;
const MAX_LANES = 8;
const MAX_RENDERED_FEATURES = 400;
const PADDING_X = 12;

export interface GenomeMapCanvasProps {
  length: number;
  topology: Topology;
  featureIndex: FeatureIndex | null;
  /** カメラ（ズーム／パンの操作対象） */
  view: ViewRange;
  /**
   * 実際に画面に出ている範囲。塩基折返し中は `view` より広い。
   * 概観窓と注釈描画に使い、操作の書き戻しには使わない。
   */
  visibleRange?: ViewRange;
  onViewChange: (view: ViewRange) => void;
  selection: Interval | null;
  onSelectionChange: (selection: Interval | null) => void;
  onFeatureActivate?: (feature: GenomicFeatureV1) => void;
  theme: 'light' | 'dark';
  overlays?: SequenceOverlayMark[];
}

interface HoverState {
  x: number;
  y: number;
  feature: GenomicFeatureV1;
}

export default function GenomeMapCanvas({
  length,
  topology,
  featureIndex,
  view,
  visibleRange,
  onViewChange,
  selection,
  onSelectionChange,
  onFeatureActivate,
  theme,
  overlays = [],
}: GenomeMapCanvasProps) {
  /** 描画用の範囲。操作は常に `view`（カメラ）へ返す */
  const drawRange = visibleRange ?? view;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dragRef = useRef<{ mode: 'pan' | 'select'; startX: number; startBase: number; moved: boolean } | null>(
    null
  );
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [hover, setHover] = useState<HoverState | null>(null);
  const pixelRatio = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) setSize({ width: rect.width, height: rect.height });
    });
    observer.observe(element);
    setSize({ width: element.clientWidth, height: element.clientHeight });
    return () => observer.disconnect();
  }, []);

  const trackWidth = Math.max(1, size.width - PADDING_X * 2);
  const pixelsPerBase = useMemo(() => {
    const bpp = basesPerPixel(drawRange, trackWidth);
    return bpp > 0 ? 1 / bpp : 0;
  }, [drawRange, trackWidth]);

  const packing = useMemo(() => {
    if (!featureIndex || pixelsPerBase <= 0) return { packed: [], laneCount: 0, droppedCount: 0 };
    const visible = featureIndex.queryForRender(drawRange, MAX_RENDERED_FEATURES);
    return packFeatureLanes(visible, {
      genomeLength: length,
      pixelsPerBase,
      maxLanes: MAX_LANES,
    });
  }, [featureIndex, drawRange, length, pixelsPerBase]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.width <= 0 || size.height <= 0) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = Math.round(size.width * pixelRatio);
    canvas.height = Math.round(size.height * pixelRatio);
    canvas.style.width = `${size.width}px`;
    canvas.style.height = `${size.height}px`;
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

    const isDark = theme === 'dark';
    const ink = isDark ? '#E2E8F0' : '#1F2937';
    const muted = isDark ? '#94A3B8' : '#6B7280';
    const rail = isDark ? '#1E293B' : '#E5E7EB';

    context.clearRect(0, 0, size.width, size.height);
    context.fillStyle = isDark ? '#0B1220' : '#F8FAFC';
    context.fillRect(0, 0, size.width, size.height);
    context.font = '11px system-ui, -apple-system, "Segoe UI", "Noto Sans JP", sans-serif';
    context.textBaseline = 'middle';

    // 分子全体と現在の表示窓
    const overviewY = 6;
    context.fillStyle = rail;
    context.fillRect(PADDING_X, overviewY, trackWidth, OVERVIEW_HEIGHT - 10);
    if (length > 0) {
      const windowX = PADDING_X + (drawRange.start / length) * trackWidth;
      const windowW = Math.max(2, ((drawRange.end - drawRange.start) / length) * trackWidth);
      context.fillStyle = isDark ? '#38BDF8' : '#2563EB';
      context.fillRect(windowX, overviewY - 2, windowW, OVERVIEW_HEIGHT - 6);
    }
    context.fillStyle = muted;
    context.textAlign = 'left';
    context.fillText(`1`, PADDING_X, overviewY + OVERVIEW_HEIGHT + 2);
    context.textAlign = 'right';
    context.fillText(formatCompactBase(length), PADDING_X + trackWidth, overviewY + OVERVIEW_HEIGHT + 2);

    // 目盛り
    const rulerY = OVERVIEW_HEIGHT + 18;
    const step = chooseTickStep(basesPerPixel(drawRange, trackWidth), 90);
    context.strokeStyle = rail;
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(PADDING_X, rulerY + RULER_HEIGHT - 6);
    context.lineTo(PADDING_X + trackWidth, rulerY + RULER_HEIGHT - 6);
    context.stroke();

    context.textAlign = 'center';
    for (const tick of tickPositions(drawRange, step, length)) {
      const x = PADDING_X + baseToPixel(tick, drawRange.start, pixelsPerBase);
      if (x < PADDING_X || x > PADDING_X + trackWidth) continue;
      context.strokeStyle = isDark ? '#334155' : '#CBD5E1';
      context.beginPath();
      context.moveTo(x, rulerY + RULER_HEIGHT - 12);
      context.lineTo(x, rulerY + RULER_HEIGHT - 6);
      context.stroke();
      context.fillStyle = muted;
      context.fillText(formatAxisLabel(tick, step), x, rulerY + 4);
    }

    // 選択帯
    const lanesTop = rulerY + RULER_HEIGHT;
    if (selection && selection.end > selection.start) {
      context.fillStyle = isDark ? 'rgba(56,189,248,0.20)' : 'rgba(37,99,235,0.14)';
      const drawSelectionSpan = (start: number, end: number) => {
        const x = PADDING_X + baseToPixel(start, drawRange.start, pixelsPerBase);
        const width = Math.max(1.5, (end - start) * pixelsPerBase);
        context.fillRect(x, lanesTop - 6, width, size.height - lanesTop);
      };
      if (selection.end > length && topology === 'circular') {
        // 原点ラップ選択は尾＋頭の 2 帯
        drawSelectionSpan(selection.start, length);
        drawSelectionSpan(0, selection.end - length);
      } else {
        drawSelectionSpan(selection.start, Math.min(selection.end, length));
      }
    }

    for (const mark of overlays) {
      const x = PADDING_X + baseToPixel(mark.start, drawRange.start, pixelsPerBase);
      if (x < PADDING_X || x > PADDING_X + trackWidth) continue;
      context.strokeStyle = mark.color;
      context.lineWidth = mark.active ? 2 : 1;
      context.beginPath();
      context.moveTo(x, lanesTop - 4);
      context.lineTo(x, size.height - 6);
      context.stroke();
    }

    // 注釈レーン
    context.textAlign = 'left';
    for (const item of packing.packed) {
      const y = lanesTop + item.lane * (LANE_HEIGHT + LANE_GAP);
      if (y + LANE_HEIGHT > size.height) continue;
      const color = featureColor(item.feature);
      context.fillStyle = color;

      for (const span of item.spans) {
        const x = PADDING_X + baseToPixel(span.start, drawRange.start, pixelsPerBase);
        const width = Math.max(1.5, (span.end - span.start) * pixelsPerBase);
        if (x + width < PADDING_X || x > PADDING_X + trackWidth) continue;
        context.fillRect(x, y, width, LANE_HEIGHT - 6);

        // 鎖の向き（色だけに意味を載せない・§0.3.4）
        if (width > 14 && item.feature.strand !== 0) {
          context.fillStyle = isDark ? '#0B1220' : '#FFFFFF';
          const arrowY = y + (LANE_HEIGHT - 6) / 2;
          const tipX = item.feature.strand === 1 ? x + width - 4 : x + 4;
          const backX = item.feature.strand === 1 ? x + width - 9 : x + 9;
          context.beginPath();
          context.moveTo(tipX, arrowY);
          context.lineTo(backX, arrowY - 3.5);
          context.lineTo(backX, arrowY + 3.5);
          context.closePath();
          context.fill();
          context.fillStyle = color;
        }
      }

      const first = item.spans[0];
      const labelX = PADDING_X + baseToPixel(first.start, drawRange.start, pixelsPerBase) + 4;
      if (canFitLabel(item.widthPx, item.feature.name, 6)) {
        context.fillStyle = ink;
        context.fillText(
          truncateLabel(item.feature.name, item.widthPx - 8, 6),
          Math.max(PADDING_X + 2, labelX),
          y + (LANE_HEIGHT - 6) / 2
        );
      }
    }

    if (packing.droppedCount > 0) {
      context.fillStyle = muted;
      context.textAlign = 'right';
      context.fillText(`ほか ${packing.droppedCount} 件`, PADDING_X + trackWidth, size.height - 8);
    }

    if (featureIndex && featureIndex.size === 0) {
      context.fillStyle = muted;
      context.textAlign = 'left';
      context.fillText('この配列に注釈はありません', PADDING_X, lanesTop + 10);
    }

    // ホバー中の注釈（U161: 色だけに頼らず名前を出す）
    if (hover) {
      const label = `${hover.feature.name}（${hover.feature.type}）`;
      const width = context.measureText(label).width + 14;
      const boxX = Math.min(Math.max(PADDING_X, hover.x - width / 2), size.width - width - PADDING_X);
      const boxY = Math.max(2, hover.y - 26);
      context.fillStyle = isDark ? 'rgba(15,23,42,0.95)' : 'rgba(255,255,255,0.97)';
      context.strokeStyle = isDark ? '#334155' : '#CBD5E1';
      context.lineWidth = 1;
      context.fillRect(boxX, boxY, width, 22);
      context.strokeRect(boxX, boxY, width, 22);
      context.fillStyle = ink;
      context.textAlign = 'left';
      context.fillText(label, boxX + 7, boxY + 11);
    }
  }, [
    featureIndex,
    hover,
    length,
    packing,
    pixelRatio,
    pixelsPerBase,
    selection,
    size.height,
    size.width,
    theme,
    trackWidth,
    drawRange,
    topology,
    length,
    overlays,
  ]);

  const baseAtClientX = useCallback(
    (clientX: number): number => {
      const canvas = canvasRef.current;
      if (!canvas) return drawRange.start;
      const rect = canvas.getBoundingClientRect();
      return pixelToBase(clientX - rect.left - PADDING_X, drawRange.start, pixelsPerBase);
    },
    [pixelsPerBase, drawRange.start]
  );

  const featureAt = useCallback(
    (clientX: number, clientY: number): GenomicFeatureV1 | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const y = clientY - rect.top;
      const lanesTop = OVERVIEW_HEIGHT + 18 + RULER_HEIGHT;
      const lane = Math.floor((y - lanesTop) / (LANE_HEIGHT + LANE_GAP));
      if (lane < 0) return null;
      const base = baseAtClientX(clientX);
      return (
        packing.packed.find(
          (item) =>
            item.lane === lane && item.spans.some((span) => base >= span.start && base < span.end)
        )?.feature ?? null
      );
    },
    [baseAtClientX, packing.packed]
  );

  // Ctrl+ホイールのブラウザズームを止めるため、非 passive の native listener を使う
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const rect = canvas.getBoundingClientRect();
        const anchor = pixelToBase(event.clientX - rect.left - PADDING_X, drawRange.start, pixelsPerBase);
        const factor = event.deltaY < 0 ? 0.8 : 1.25;
        onViewChange(zoomRange(view, factor, anchor, length));
        return;
      }
      event.preventDefault();
      const delta = (view.end - view.start) * 0.15 * (event.deltaY > 0 ? 1 : -1);
      onViewChange(panRange(view, delta, length, topology));
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [drawRange.start, length, onViewChange, pixelsPerBase, topology, view]);

  const handleMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    dragRef.current = {
      mode: event.shiftKey ? 'select' : 'pan',
      startX: event.clientX,
      startBase: baseAtClientX(event.clientX),
      moved: false,
    };
    if (event.shiftKey) onSelectionChange(null);
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    if (!drag) {
      const feature = featureAt(event.clientX, event.clientY);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      setHover(
        feature ? { feature, x: event.clientX - rect.left, y: event.clientY - rect.top } : null
      );
      return;
    }

    if (Math.abs(event.clientX - drag.startX) > 2) drag.moved = true;
    if (drag.mode === 'select') {
      const base = baseAtClientX(event.clientX);
      onSelectionChange({
        start: Math.floor(Math.min(drag.startBase, base)),
        end: Math.ceil(Math.max(drag.startBase, base)),
      });
      return;
    }
    const delta = drag.startBase - baseAtClientX(event.clientX);
    if (delta !== 0) onViewChange(panRange(view, delta, length, topology));
  };

  const handleMouseUp = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag || drag.moved) return;
    const feature = featureAt(event.clientX, event.clientY);
    if (feature && onFeatureActivate) onFeatureActivate(feature);
  };

  return (
    <div ref={containerRef} className="ga-map-canvas" data-testid="gene-analysis-map-canvas">
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => {
          dragRef.current = null;
          setHover(null);
        }}
      />
    </div>
  );
}
